"use client";

import { getOnRoadPrice } from "@/utils/onRoadPrice";
import { formatEmiCurrency } from "@/lib/utils/emi";
import ImageWrapper from "../ImageWrapper";

export interface CityPriceGridCity {
    name: string;
    slug: string;
    state?: { slug?: string; [key: string]: any };
    cityTier?: string | null;
}

export default function CityPriceGridClient({
    title = "Price in India",
    subtitle,
    variantData = null,
    brandSlug = "",
    modelSlug = "",
    cities = [],
}: {
    title?: string;
    subtitle?: string;
    variantData?: any | null;
    brandSlug?: string;
    modelSlug?: string;
    cities?: CityPriceGridCity[];
}) {
    if (!cities.length) return null;

    const brandModelName = title.replace(/\s*Price in India\s*$/i, "").trim();

    const citiesWithPrice = cities.map((c) => {
        const onRoadPrice = variantData
            ? getOnRoadPrice(variantData, { name: c.name, slug: c.slug, state: c.state || {}, cityTier: c.cityTier } as any)?.onRoadPrice
            : undefined;
        const priceText = onRoadPrice ? `₹${formatEmiCurrency(Number(onRoadPrice))}` : "—";
        return { name: c.name, slug: c.slug, price: priceText };
    });

    return (
        <section className="rounded-xl border border-[#e6f4f1] mb-6 overflow-hidden">
            {/* Colored header */}
            <div className="bg-[#008C8B0D] px-5 py-4 flex items-start justify-between gap-3 border-b border-[#e6f4f1]">
                <div>
                    <h2 className="text-base md:text-lg font-bold text-[#0b6b66] leading-tight m-0">
                        {title}
                    </h2>
                    {subtitle && (
                        <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
                    )}
                </div>
                {/* Icon badge */}
                <div className="w-9 h-9 flex-shrink-0 rounded-lg bg-[#008C8C15] flex items-center justify-center">
                    <ImageWrapper
                        src={"/delhi-location.svg"}
                        alt="delhi-location Icon"
                        title="delhi-location Icon"
                        width={18}
                        height={18}
                        cdnBaseUrl={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}`}
                        className=""
                    />
                </div>
            </div>

            {/* White cards area */}
            <div className="bg-white p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {citiesWithPrice.map((c) => (
                        <a
                            key={c.name}
                            href={`/${brandSlug}/${modelSlug}/price-in-${c.slug}`}
                            title={brandModelName ? `${brandModelName} Price in ${c.name}` : `Price in ${c.name}`}
                            className="flex items-center justify-between px-4 py-3 rounded-lg border border-gray-100 hover:border-[#b2e0db] hover:shadow-sm transition-all no-underline group"
                        >
                            <div>
                                <div className="text-sm font-semibold text-[#0b6b66] group-hover:text-[#08514d]">
                                    {c.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 font-medium">{c.price}</div>
                            </div>
                            <svg
                                className="ml-3 flex-shrink-0 text-[#0b7f7a]"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M9 6L15 12L9 18" stroke="#0b7f7a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}
